import React, { useCallback, useEffect, useMemo, useState } from 'react';  
import { Table, Modal, Typography, Space, Tag, Button, Input, Skeleton, Select } from 'antd';  
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';
import { searchApplications } from '../../api/application';  
import { getRoundPaidTotals } from '../../api/invoice';
import ApplicationTable from '../../components/Applications/applicationsTable';  
import { exportPaidApplications } from '../../utils/schedulingUtils/exportToXlsxUtils';
import { loadState } from '../../stores/store';  
  
const { Title } = Typography;  
  
const Entries = () => {  
  const [applications, setApplications] = useState([]);  
  const [loading, setLoading] = useState(true);  
  const [modalVisible, setModalVisible] = useState(false);  
  const [selectedParticipant, setSelectedParticipant] = useState(null);  
  const [searchText, setSearchText] = useState('');  
  const [paidFilter, setPaidFilter] = useState('all');
  const [roundFilter, setRoundFilter] = useState(null);  
  const state = useMemo(() => loadState(), []);  
  
  const clearSearch = () => setSearchText(''); 
  
  // 1. Fetch all applications  
  const fetchApplications = useCallback(async () => {  
    setLoading(true);  
    try {  
      // For admin, maybe filter by province (adapt as needed)  
      const fetchedApps = await searchApplications();  
      console.log(fetchedApps);
      setApplications(fetchedApps);
    } catch (e) {  
      setApplications([]);  
    } finally {  
      setLoading(false);  
    }  
  }, [state.user.role, state.user.activeProvince]);  
  
  useEffect(() => {  
    fetchApplications();  
  }, [fetchApplications]);  
  
  // 2. Group applications by participant (solo or group)  
  const groupedEntries = useMemo(() => {  
    const solo = {};  
    const groups = {};  
    for (const app of applications) {  
      if (app.contestant && !app.group) {  
        const id = app.contestant._id;  
        if (!solo[id]) solo[id] = { contestant: app.contestant, applications: [] };  
        solo[id].applications.push(app);  
      } else if (app.group) {  
        const id = app.group._id;  
        if (!groups[id]) groups[id] = { group: app.group, applications: [] };  
        groups[id].applications.push(app);  
      }  
    }  
    const filterFn = (e) => {  
      let match = true;  
      if (searchText !== '') {  
        if (e.contestant)  
          match = `${e.contestant.first_name} ${e.contestant.last_name}`.toLowerCase().includes(searchText.toLowerCase());  
        else if (e.group)  
          match = e.group.group_name.toLowerCase().includes(searchText.toLowerCase());  
        else  
          match = false;  
      }  
      if (paidFilter !== 'all') {  
        const allPaid = e.applications.length > 0 && e.applications.every(app => app.paid);  
        if (paidFilter === 'paid' && !allPaid) match = false;  
        if (paidFilter === 'notpaid' && allPaid) match = false;  
      }  
      return match;  
    };  
    // Add roundFilter  
    if (roundFilter !== null) {  
      const roundNum = roundFilter;  
      const roundMatch = e => e.applications.some(app => app.round === roundNum);  
      return [  
        ...Object.values(solo).filter(filterFn).filter(roundMatch),  
        ...Object.values(groups).filter(filterFn).filter(roundMatch)  
      ];  
    }  
    return [  
      ...Object.values(solo).filter(filterFn),  
      ...Object.values(groups).filter(filterFn)  
    ];  
  }, [applications, searchText, paidFilter, roundFilter]);  
  
  // 3. Table Columns  
  const columns = [  
    {  
      title: 'Contestant',  
      dataIndex: 'contestant',  
      key: 'contestant',  
      render: (_, entry) =>  
        entry.contestant ? (  
          <Button type="link"  
            onClick={() => { setSelectedParticipant(entry); setModalVisible(true); }}>  
            {entry.contestant.first_name} {entry.contestant.last_name}  
          </Button>  
        ) : (  
          <Button type="link"  
            onClick={() => { setSelectedParticipant(entry); setModalVisible(true); }}>  
            {entry.group.group_name}  
          </Button>  
        ),  
      sorter: (a, b) => {  
        const nameA = a.contestant  
          ? `${a.contestant.first_name} ${a.contestant.last_name}`  
          : a.group.group_name;  
        const nameB = b.contestant  
          ? `${b.contestant.first_name} ${b.contestant.last_name}`  
          : b.group.group_name;  
        return nameA.localeCompare(nameB);  
      }  
    },  
    {  
      title: 'Type',  
      key: 'type',  
      render: (entry) => entry.contestant ? 'Solo' : 'Group',  
      filters: [  
        { text: 'Solo', value: 'solo' },  
        { text: 'Group', value: 'group' }  
      ],  
      onFilter: (value, entry) =>  
        (value === 'solo' && entry.contestant) || (value === 'group' && entry.group)  
    },  
    {  
      title: 'Age',  
      key: 'group_age',  
      render: (entry) => {  
        const ages = entry.applications.map(app => app.group_age).filter(Boolean);  
        return ages.length ? ages[0] : '';  
      }  
    },  
    {  
      title: 'Paid',  
      key: 'paid',  
      render: (entry) => {  
        const allPaid = entry.applications.length > 0 && entry.applications.every(app => app.paid);  
        return allPaid ? <Tag color="green">Paid</Tag> : <Tag color="red">Not Paid</Tag>;  
      },  
      filters: [  
        { text: 'Paid', value: 'paid' },  
        { text: 'Not Paid', value: 'notpaid' }  
      ],  
      onFilter: (value, entry) => {  
        const allPaid = entry.applications.length > 0 && entry.applications.every(app => app.paid);  
        return value === 'paid' ? allPaid : !allPaid;  
      }  
    }  
  ];  
  
  return (  
    <div>  
      {/* TITLE */}  
      <div  
      style={{  
        display: 'flex',  
        alignItems: 'center',  
        justifyContent: 'space-between',  
        marginBottom: 0,  
      }}  
    >  
      <Title level={4} style={{ margin: 0 }}>  
        Contestants  
      </Title>  
      <Button  
        onClick={async () => {
          try {
            const invoices = await getRoundPaidTotals(2);
            const invoiceAmountMap = invoices.reduce((totals, invoice) => {
              const participantKey = invoice.group
                ? `group_${invoice.group}`
                : invoice.contestant
                  ? `contestant_${invoice.contestant}`
                  : null;

              if (participantKey) {
                totals[participantKey] = (totals[participantKey] || 0) + (Number(invoice.fee?.total_fee_paid) || 0);
              }
              return totals;
            }, {});

            exportPaidApplications(applications, invoiceAmountMap);
          } catch (error) {
            window.alert('Unable to fetch paid invoice totals. The export was not created.');
          }
        }}
        type="primary"  
      >  
        Export Paid Applications  
      </Button>  
    </div>   
    
      {/* FILTERS - now under the title */}  
      <Space style={{ margin: '12px 0 16px 0' }}>  
        <span>Filter by Round:</span>  
        <Select  
          style={{ width: 140, marginRight: 16 }}  
          value={roundFilter !== null ? roundFilter : ""}  
          onChange={val => setRoundFilter(val === "" ? null : val)}  
          options={[  
            { label: 'All Rounds', value: "" },  
            { label: 'Round 1', value: 1 },  
            { label: 'Round 2', value: 2 },  
            { label: 'Round 3', value: 3 }  
          ]}  
          placeholder="Select round"  
        />  
        <span>Filter by Payment:</span>  
        <Select  
          style={{ width: 140 }}  
          value={paidFilter}  
          onChange={setPaidFilter}  
          options={[  
            { label: 'All', value: 'all' },  
            { label: 'Paid', value: 'paid' },  
            { label: 'Not Paid', value: 'notpaid' }  
          ]}  
          placeholder="Select payment"  
        />  
      </Space>  
    
      {/* SEARCH INPUT */}  
      <Input  
        placeholder="Search by contestant name or group"  
        value={searchText}  
        onChange={e => setSearchText(e.target.value)}  
        style={{ marginBottom: 16, width: '100%' }}  
        suffix={  
          <>  
            <SearchOutlined style={{ marginRight: '8px' }} />  
            {searchText && (  
              <CloseOutlined onClick={clearSearch} style={{ cursor: 'pointer' }} />  
            )}  
          </>  
        }  
      />  
    
      {/* TABLE */}  
      <Table  
        columns={columns}  
        dataSource={groupedEntries}  
        loading={loading}  
        rowKey={entry =>  
          entry.contestant ? entry.contestant._id : entry.group._id  
        }  
        pagination={{ pageSize: 20 }}  
      />  
    
      {/* MODAL ... */}  
      <Modal  
        open={modalVisible}  
        onCancel={() => setModalVisible(false)}  
        title={  
          selectedParticipant  
            ? selectedParticipant.contestant  
              ? `Applications for ${selectedParticipant.contestant.first_name} ${selectedParticipant.contestant.last_name}`  
              : `Applications for group "${selectedParticipant.group.group_name}"`  
            : ''  
        }  
        width={'90vw'}  
        destroyOnClose  
        footer={null}  
      >  
        {selectedParticipant ? (  
          <ApplicationTable  
            loading={loading}  
            applications={selectedParticipant.applications}  
            isAdmin={state.user.role === 'admin'}  
            onApplicationUpdate={fetchApplications}  
          />  
        ) : (  
          <Skeleton />  
        )}  
      </Modal>  
    </div>  
  );  
};  
  
export default Entries;  